import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, FileText, Trash2, RefreshCw, CheckCircle2, Loader2, AlertCircle, Slack, Mail, Database, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Sources() {
  const { canWrite } = useAuth();
  const [sources, setSources] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);
  const [manual, setManual] = useState({ title: "", content: "", department: "General" });

  const load = async () => {
    const { data } = await api.get("/sources");
    setSources(data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/sources/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded, categorizing…");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!manual.title || !manual.content) return;
    setBusy(true);
    try {
      await api.post("/sources/manual", manual);
      toast.success("Added, categorizing…");
      setManual({ title: "", content: "", department: "General" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this source and all extracted knowledge?")) return;
    await api.delete(`/sources/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-8 max-w-6xl">
      <header className="mb-8">
        <p className="eyebrow mb-3">Sources</p>
        <h1 className="font-display text-3xl">Ingest knowledge</h1>
        <p className="mt-2 text-muted-foreground">Upload documents or paste content directly. Claude will structure it automatically.</p>
      </header>

      {!canWrite && (
        <div className="mb-6 rounded-md border border-border/60 bg-secondary/40 p-4 flex items-start gap-3" data-testid="sources-viewer-banner">
          <EyeOff className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
          <div>
            <p className="text-sm font-medium">Viewer access</p>
            <p className="text-xs text-muted-foreground mt-0.5">You can browse existing sources but can't upload or import. Ask an Admin to bump your role to <span className="font-medium">Member</span>.</p>
          </div>
        </div>
      )}

      <Tabs defaultValue="upload" className="mb-10">
        <TabsList data-testid="sources-tabs">
          <TabsTrigger value="upload" data-testid="sources-tab-upload">Upload file</TabsTrigger>
          <TabsTrigger value="manual" data-testid="sources-tab-manual">Manual entry</TabsTrigger>
          <TabsTrigger value="import" data-testid="sources-tab-import">Importers</TabsTrigger>
        </TabsList>

        <TabsContent value="upload">
          <div className="card-flat p-8 mt-4">
            <div className="border border-dashed border-border rounded-md p-10 text-center">
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-4" />
              <p className="mb-2 font-medium">Drop a document or click to browse</p>
              <p className="text-xs text-muted-foreground mb-6">PDF, DOCX, TXT, MD, CSV, JSON — up to 15 MB</p>
              <input
                ref={fileInput}
                type="file"
                onChange={onUpload}
                accept=".pdf,.docx,.txt,.md,.csv,.json"
                className="hidden"
                data-testid="sources-file-input"
              />
              <Button
                onClick={() => fileInput.current?.click()}
                disabled={busy || !canWrite}
                data-testid="sources-upload-btn"
                className="rounded-full"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Select file
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="manual">
          <form onSubmit={submitManual} className="card-flat p-8 mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="title">Title</Label>
              <Input id="title" required value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} placeholder="e.g. Refund policy 2026" data-testid="manual-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="dep">Department</Label>
                <Input id="dep" value={manual.department} onChange={(e) => setManual({ ...manual, department: e.target.value })} placeholder="Sales / Support / HR…" data-testid="manual-department-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="content">Content</Label>
              <Textarea id="content" required rows={10} value={manual.content} onChange={(e) => setManual({ ...manual, content: e.target.value })} placeholder="Paste your process, policy, decision rule, roles, or FAQ content here…" data-testid="manual-content-input" />
            </div>
            <Button type="submit" disabled={busy || !canWrite} data-testid="manual-submit-btn" className="rounded-full">
              {busy ? "Adding…" : "Add to brain"}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="import">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <ImporterCard
              kind="slack"
              title="Slack export"
              desc="Upload a Slack export .zip or a channel .json file. Every message becomes knowledge."
              icon={Slack}
              accept=".zip,.json"
              onDone={load}
              busy={busy}
              setBusy={setBusy}
            />
            <ImporterCard
              kind="email"
              title="Email (.eml / .mbox)"
              desc="Import single .eml files or entire mailboxes. Subject, sender, and body extracted."
              icon={Mail}
              accept=".eml,.mbox"
              onDone={load}
              busy={busy}
              setBusy={setBusy}
            />
            <ImporterCard
              kind="database"
              title="Database export"
              desc="Upload a CSV export from any database. Each row becomes a knowledge record."
              icon={Database}
              accept=".csv"
              onDone={load}
              busy={busy}
              setBusy={setBusy}
            />
          </div>
        </TabsContent>
      </Tabs>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl">Library</h2>
          <button onClick={load} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="sources-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        {sources.length === 0 ? (
          <div className="card-flat p-10 text-center text-muted-foreground text-sm">
            No sources yet. Upload a file or add manual entry above.
          </div>
        ) : (
          <div className="card-flat divide-y divide-border/60">
            {sources.map((s) => (
              <div key={s.id} className="p-4 flex items-center gap-4" data-testid={`source-row-${s.id}`}>
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{s.filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.kind} · {new Date(s.created_at).toLocaleString()} {s.entry_count != null && `· ${s.entry_count} entries`}
                  </p>
                </div>
                <StatusBadge status={s.status} />
                {canWrite && (
                  <button onClick={() => del(s.id)} className="text-muted-foreground hover:text-destructive" data-testid={`source-delete-${s.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    processed: { icon: CheckCircle2, cls: "text-emerald-500", label: "Ready" },
    processing: { icon: Loader2, cls: "text-amber-500 animate-spin", label: "Processing" },
    failed: { icon: AlertCircle, cls: "text-destructive", label: "Failed" },
  };
  const m = map[status] || map.processing;
  return (
    <span className={`flex items-center gap-1.5 text-xs ${m.cls === "text-destructive" ? "text-destructive" : m.cls.split(" ")[0]}`}>
      <m.icon className={`w-3.5 h-3.5 ${status === "processing" ? "animate-spin" : ""}`} />
      {m.label}
    </span>
  );
}

function ImporterCard({ kind, title, desc, icon: Icon, accept, onDone, busy, setBusy }) {
  const ref = useRef(null);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/sources/import/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${title} imported`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="card-flat p-6 flex flex-col" data-testid={`importer-${kind}`}>
      <Icon className="w-5 h-5 mb-3" />
      <h3 className="font-display text-lg mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground mb-6 leading-relaxed flex-1">{desc}</p>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={onPick} data-testid={`importer-${kind}-input`} />
      <Button onClick={() => ref.current?.click()} disabled={busy} className="rounded-full w-fit" data-testid={`importer-${kind}-btn`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
        Import
      </Button>
    </div>
  );
}
